import { DateRange, isDateRange } from '@daypicker/react'
import { IconCalendarMonth } from '@tabler/icons-react'
import { Dayjs } from 'dayjs'
import capitalize from 'lodash/capitalize'
import { useState } from 'react'

import { MonthPicker, Unit } from '../../../../components'
import { SizeOptions } from '../../../../types/types'
import { DATE_TEMPLATES, day, format, getFridayInWeek, getToday, isSame } from '../../../../utils/extendedDayjs'
import { AstraIcon } from '../../../AstraIcon/AstraIcon'
import { AstraPopover } from '../../../AstraPopover/AstraPopover'
import { AstraButton } from '../../AstraButton/AstraButton'
import { AstraDayPicker } from '../AstraDayPicker/AstraDayPicker'
import { AstraWeekPicker } from '../AstraWeekPicker/AstraWeekPicker'

type PropsBase = {
    placeholder?: string
    unit: Unit
    value: Dayjs | undefined
    dataTest?: string
    dataTestInput?: string
    side?: 'top' | 'right' | 'bottom' | 'left'
    popoverSize?: SizeOptions
    buttonFullWidth?: boolean
    disabled?: boolean
    startMonth?: Dayjs
}

type AllowClearProps = {
    allowClear: true
    onChange: (value: Dayjs | undefined) => void
}
type NoClearProps = {
    allowClear: false
    onChange: (value: Dayjs) => void
}

type DefaultClear = {
    allowClear?: undefined
    onChange: (value: Dayjs) => void
}

type AstraDatePopoverProps = PropsBase & (AllowClearProps | NoClearProps | DefaultClear)

const DEFAULT_PICKER_PROPS = {
    showOutsideDays: true,
    weekdaysOnly: true,
    showTodayButton: true,
}

export function getFormattedDate(value?: Dayjs, unit?: Unit) {
    if (!value) return

    if (unit === 'month') {
        return `${capitalize(format(value, 'MMM'))} ${format(value, 'YYYY')}`
    }

    if (unit === 'day') {
        return `${capitalize(format(value, 'dddd'))} ${format(value, DATE_TEMPLATES.norwegianDate)}`
    }

    const firstDayOfWeek = value.startOf('isoWeek')
    const lastDayOfWorkingWeek = getFridayInWeek(value)
    const isOverlappingMonth = !isSame(firstDayOfWeek, lastDayOfWorkingWeek, 'month')

    const monthLabel = isOverlappingMonth
        ? `${capitalize(format(firstDayOfWeek, 'MMM'))}–${capitalize(format(lastDayOfWorkingWeek, 'MMM'))}`
        : capitalize(format(value, 'MMM'))

    return `${monthLabel} ${format(value, 'YYYY')} / Uke ${value.isoWeek()}`
}

export const AstraDatePopover = ({
    placeholder,
    value,
    onChange,
    popoverSize,
    unit,
    buttonFullWidth = false,
    dataTest = 'week-picker-popover',
    dataTestInput = 'day-picker-input',
    side,
    disabled,
    allowClear,
    startMonth,
}: AstraDatePopoverProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const today = getToday()

    const handleSelect = (selected?: DateRange | Date) => {
        if (!selected) {
            // selected will be undefined when already selcted date is clicked again
            // in that case; don't close the popover if clearing the value.
            // If not clearing, keep the selected date and close the popover
            if (allowClear) {
                onChange(undefined)
            } else {
                setIsOpen(false)
            }
        } else if (isDateRange(selected)) {
            if (selected.from) {
                onChange(day(selected.from).startOf('isoWeek'))
            }
            setIsOpen(false)
        } else {
            onChange(day(selected))
            setIsOpen(false)
        }
    }

    const handleWeekTodayClicked = () => {
        onChange(today.startOf('isoWeek'))
        setIsOpen(false)
    }

    const handleTodayClicked = () => {
        onChange(today)
        setIsOpen(false)
    }

    const handleClear = () => {
        handleSelect(undefined)
        setIsOpen(false)
    }

    const handleMonthPickerChange = (selected?: Dayjs) => {
        if (!selected && allowClear) {
            handleClear()
        } else {
            handleSelect(selected?.toDate())
        }
    }
    const PickerByUnit = {
        month: <MonthPicker value={value} onChange={handleMonthPickerChange} allowClear={allowClear} />,
        week: (
            <AstraWeekPicker
                size={popoverSize}
                mode="single"
                numberOfMonths={1}
                defaultMonth={value?.toDate() ?? today.toDate()}
                {...DEFAULT_PICKER_PROPS}
                selected={{
                    from: value?.startOf('isoWeek').toDate(),
                    to: value?.endOf('isoWeek').toDate(),
                }}
                onSelect={handleSelect}
                onTodayButtonClick={handleWeekTodayClicked}
                disabled={disabled}
                onClearClick={allowClear ? handleClear : undefined}
                startMonth={startMonth?.toDate()}
            />
        ),
        day: (
            <AstraDayPicker
                size={popoverSize}
                fullWidth
                mode="single"
                required={false}
                defaultMonth={value?.toDate() ?? today.toDate()}
                {...DEFAULT_PICKER_PROPS}
                onTodayButtonClick={handleTodayClicked}
                selected={value?.toDate()}
                onClearClick={allowClear ? handleClear : undefined}
                onSelect={handleSelect}
                disabled={disabled}
                startMonth={startMonth?.toDate()}
            />
        ),
    }

    return (
        <AstraPopover
            noPadding
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            data-test={dataTest}
            side={side}
            popoverContent={<div className="w-[300px]">{PickerByUnit[unit]}</div>}
        >
            <div className={buttonFullWidth ? 'w-full' : 'w-fit'}>
                <AstraButton
                    data-test={dataTestInput}
                    variant="secondary"
                    fullWidth
                    label={value ? getFormattedDate(value, unit) : placeholder}
                    endIcon={<AstraIcon icon={IconCalendarMonth} />}
                />
            </div>
        </AstraPopover>
    )
}
